import { Component, ChangeDetectionStrategy, ElementRef, ViewChild, AfterViewInit, OnDestroy, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface HiringStep {
  number: string;
  title: string;
  subtitle: string;
  features: string[];
  mockupId: 'm1' | 'm2' | 'm3' | 'm4' | 'm5' | 'm6';
  buttonText: string;
  colorClass: string;
}

@Component({
  selector: 'app-recruiter-hiring-process',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './recruiter-hiring-process.component.html',
  styleUrls: ['./recruiter-hiring-process.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RecruiterHiringProcessComponent implements AfterViewInit, OnDestroy {
  @ViewChild('workflowGrid') workflowGrid!: ElementRef<HTMLElement>;
  @ViewChild('funnelPipeline') funnelPipeline!: ElementRef<HTMLElement>;

  private scrollIntervals: any[] = [];
  private pauseTimeouts: any[] = [];

  constructor(private ngZone: NgZone) { }
  readonly steps: HiringStep[] = [
    {
      number: '01',
      title: 'Create & Post Job on BeExpert',
      subtitle: 'Create detailed job posts and publish in minutes.',
      features: [
        'Role, Skills & Experience',
        'Salary, Location & Eligibility',
        'Custom Hiring Workflow',
        'Assessment & Interview Setup '
      ],
      mockupId: 'm1',
      buttonText: 'Create & Post Job →',
      colorClass: 'indigo'
    },
    {
      number: '02',
      title: 'Manage Candidates (Job-wise)',
      subtitle: 'View and manage all applicants for each job in one place.',
      features: [
        'Job-wise candidate tracking',
        'Filter by status, skills & location',
        'Bulk Upload & Invite',
        'Real-time candidate updates'
      ],
      mockupId: 'm2',
      buttonText: 'Manage Candidates →',
      colorClass: 'blue'
    },
    {
      number: '03',
      title: 'Proctored Assessment Round',
      subtitle: 'Conduct smart, monitored assessments with real-time insights.',
      features: [
        'Live proctoring & monitoring',
        'Anti-cheating & security',
        'Auto evaluation & scoring',
        'Detailed performance reports'
      ],
      mockupId: 'm3',
      buttonText: 'View Assessment Reports →',
      colorClass: 'emerald'
    },
    {
      number: '04',
      title: 'Proctored AI Interview',
      subtitle: 'AI-led interviews to evaluate skills, communication & behavior.',
      features: [
        'Live proctoring & anti-cheating',
        'Real-time voice & video analysis',
        'Communication, technical & behavioral scoring',
        'Detailed interview evaluation report'
      ],
      mockupId: 'm4',
      buttonText: 'View Interview Reports →',
      colorClass: 'purple'
    },
    {
      number: '05',
      title: 'Review Candidates',
      subtitle: 'Review candidate reports, evaluate performance, and Shortlist the best talent with confidence.',
      features: [
        'Comprehensive Candidate Reports',
        'Assessment & AI Interview Insights',
        'Detailed Performance Analytics',
        'Shortlist Top Candidates'
      ],
      mockupId: 'm5',
      buttonText: 'Review & Shortlist →',
      colorClass: 'amber'
    },
    {
      number: '06',
      title: 'Hire the Best Candidate',
      subtitle: 'Select the most suitable candidate and hire with confidence.',
      features: [
        'AI-Driven Candidate Evaluation',
        'Final Candidate Selection',
        'Faster Hiring Decisions',
        'Build Your Team'
      ],
      mockupId: 'm6',
      buttonText: 'Hire Candidate →',
      colorClass: 'rose'
    }
  ];

  readonly funnelSteps = [
    {
      title: 'Applied',
      count: '320',
      label: 'Candidates',
      subtitle: 'Applications received for the job',
      color: 'indigo',
      icon: 'users',
      isPill: false
    },
    {
      title: 'Proctored Assessment Cleared',
      count: '120',
      label: 'Candidates',
      subtitle: 'Report sent to recruiter',
      color: 'blue',
      icon: 'clipboard',
      isPill: true
    },
    {
      title: 'Proctored Interview Cleared',
      count: '45',
      label: 'Candidates',
      subtitle: 'Report sent to recruiter',
      color: 'purple',
      icon: 'video',
      isPill: true
    },
    {
      title: 'Shortlisted',
      count: '10',
      label: 'Candidates',
      subtitle: 'Best performers shortlisted',
      color: 'amber',
      icon: 'star',
      isPill: false
    },
    {
      title: 'Hired',
      count: '1',
      label: 'Candidate',
      subtitle: 'The perfect fit hired!',
      color: 'emerald',
      icon: 'trophy',
      isPill: false
    }
  ];

  ngAfterViewInit() {
    this.setupAutoScroll(this.workflowGrid?.nativeElement, 3500); // 3.5 seconds
    this.setupAutoScroll(this.funnelPipeline?.nativeElement, 4200); // 4.2 seconds
  }

  ngOnDestroy() {
    this.scrollIntervals.forEach(interval => clearInterval(interval));
    this.pauseTimeouts.forEach(timeout => clearTimeout(timeout));
  }

  private setupAutoScroll(element: HTMLElement | undefined, intervalMs: number = 3000) {
    if (!element) return;

    this.ngZone.runOutsideAngular(() => {
      let isPaused = false;
      let intervalId: any;

      const startScroll = () => {
        intervalId = setInterval(() => {
          if (isPaused || window.innerWidth >= 768) return;

          let nextScroll = element.scrollLeft + element.clientWidth * 0.7;

          // Check if we are already at the maximum scroll position
          const maxScroll = element.scrollWidth - element.clientWidth;
          if (Math.ceil(element.scrollLeft) >= maxScroll - 10) {
            nextScroll = 0; // Wrap to start
          } else if (nextScroll > maxScroll) {
            nextScroll = maxScroll; // Clamp to max if overshooting
          }

          element.scrollTo({ left: nextScroll, behavior: 'smooth' });
        }, intervalMs);
      };

      const pauseScroll = () => {
        isPaused = true;
        this.pauseTimeouts.push(
          setTimeout(() => {
            isPaused = false;
          }, 5000)
        );
      };

      // Pause briefly if the user manually touches or interacts with the slider
      element.addEventListener('touchstart', pauseScroll, { passive: true });
      element.addEventListener('pointerdown', pauseScroll, { passive: true });

      startScroll();
      this.scrollIntervals.push(intervalId);
    });
  }
}
